import { useEffect, useState } from "react";

/**
 * AmbientAtmosphere: マップ全体に大阪・関西の自然の息遣いをもたらす環境演出。
 * - ゆっくり漂う雲（多層パララックス）
 * - 空港・海沿いの波のきらめき（SVGアニメーション）
 * - 舞い散る桜の微粒子
 * - prefers-reduced-motion を尊重してアニメーションを停止可能
 */
export function AmbientAtmosphere() {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  if (reducedMotion) {
    return null;
  }

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden select-none z-0">
      {/* ── 1. 流れる雲（多層CSSアニメーション） ── */}
      <div className="absolute inset-0 opacity-40">
        {/* 上層の大きな白い雲 */}
        <div
          className="absolute -top-10 -left-40 w-96 h-48 rounded-full bg-white/70 blur-3xl"
          style={{ animation: "floatCloud1 45s linear infinite" }}
        />
        <div
          className="absolute top-48 -left-60 w-80 h-40 rounded-full bg-white/60 blur-2xl"
          style={{ animation: "floatCloud2 60s linear infinite" }}
        />
        <div
          className="absolute top-96 -left-52 w-[480px] h-52 rounded-full bg-[#fff9ea]/50 blur-3xl"
          style={{ animation: "floatCloud3 50s linear infinite" }}
        />
      </div>

      {/* ── 2. 海沿い・空港付近（KIX）の波のきらめき ── */}
      <svg
        className="absolute left-6 top-16 w-[420px] h-[320px] opacity-60"
        viewBox="0 0 420 320"
        fill="none"
        aria-hidden
      >
        <path
          d="M 30 180 Q 90 170 160 185 T 300 175"
          stroke="#72a8c4"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="8 12"
          className="animate-[dash_6s_linear_infinite]"
        />
        <path
          d="M 60 210 Q 130 195 210 215 T 360 200"
          stroke="#90bfd4"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="6 14"
          className="animate-[dash_8s_linear_infinite_reverse]"
        />
        <path
          d="M 20 240 Q 100 230 190 245 T 320 235"
          stroke="#aed4e4"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="6 10"
          className="animate-[dash_7s_linear_infinite]"
        />
      </svg>

      {/* ── 3. 桜の花びらの微粒子（軽量CSS） ── */}
      <div className="absolute inset-0">
        {[
          { x: "15%", y: "20%", delay: "0s", dur: "7s", s: "🌸" },
          { x: "42%", y: "15%", delay: "2.5s", dur: "9s", s: "🌸" },
          { x: "78%", y: "28%", delay: "1.2s", dur: "8s", s: "✨" },
          { x: "28%", y: "65%", delay: "3.8s", dur: "7.5s", s: "🌸" },
          { x: "65%", y: "72%", delay: "4.5s", dur: "8.5s", s: "✨" },
          { x: "85%", y: "80%", delay: "2.0s", dur: "9.5s", s: "🌸" },
        ].map((p, i) => (
          <div
            key={i}
            className="absolute text-xs opacity-70"
            style={{
              left: p.x,
              top: p.y,
              animation: `petalFall ${p.dur} ease-in-out infinite`,
              animationDelay: p.delay,
            }}
          >
            {p.s}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes floatCloud1 {
          0% { transform: translateX(0); }
          100% { transform: translateX(2200px); }
        }
        @keyframes floatCloud2 {
          0% { transform: translateX(0); }
          100% { transform: translateX(2300px); }
        }
        @keyframes floatCloud3 {
          0% { transform: translateX(0); }
          100% { transform: translateX(2400px); }
        }
        @keyframes petalFall {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 0; }
          20% { opacity: 0.8; }
          80% { opacity: 0.8; }
          100% { transform: translate(60px, 90px) rotate(180deg); opacity: 0; }
        }
        @keyframes dash {
          to { stroke-dashoffset: 60; }
        }
      `}</style>
    </div>
  );
}
